import React from 'react';
import { Component, Position, Container, FilterCombo, getUnixDate, PuzzleButton } from "puzzle";
import { DUE_DATE_INFO, PROPS_PHASE_DUE_DATE, STATE_PHASE_DUE_DATE } from "./Types";
import _ from "lodash";
import { DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { ComboBoxChangeEvent, ComboBoxFocusEvent, ComboBoxBlurEvent } from '@progress/kendo-react-dropdowns';
import LocalizedDatePicker from "../../../util/LocalizedDatePicker/LocalizedDatePicker";
import { Keys } from "@progress/kendo-react-common";
import FocusControl from "./FocusControl";

export class PhaseDueDates extends Component<PROPS_PHASE_DUE_DATE, STATE_PHASE_DUE_DATE> {
  protected dontResetPhases: boolean = false;
  calendarClick: boolean = false; // tracks if click came form Calendar and prevents blur (localizedDatePickerOnBlur logic)
  domElement: React.RefObject<HTMLDivElement> = React.createRef();
  focusElement: DatePickerChangeEvent | undefined = undefined;

  static defaultProps = {
    allowEdit: true,
    phasesData: [],
    targetLanguages: [],
    targetLanguageInfos: [],
    multipleBatches: false,
    showComponent: false
  };

  public state: STATE_PHASE_DUE_DATE = {
    calculatedDueDates: [],
    currentValue: null,
    existingDueDatesInfo: {},
    index: null,
    targetLanguage: ''
  };

  /**
   * @inheritDoc
   */
  moduleDidMount(): void {
    const { targetLanguages, targetLanguageInfos } = this.props;
    let targetLanguage = targetLanguages[0]?.targetLanguage;
    let existingDueDatesInfo = this.parseExistingDueDates(targetLanguageInfos);
    // @ts-ignore
    let currentInfo = existingDueDatesInfo[targetLanguage];
    let { calculatedDueDates } = this.state;
    if (currentInfo?.length) {
      calculatedDueDates = currentInfo;
      this.dispatch('unmaskModuleOnly', Position.end);
    } else {
      this.getCalculatedDueDates();
    }
    this.setState({
      calculatedDueDates,
      targetLanguage,
      existingDueDatesInfo
    });
  };

  /**
   * @inheritDoc
   */
  componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<any>, snapshot?: any): void {
    const propsChanged = !_.isEqual(prevProps, this.props);
    if (propsChanged) {
      const { batchDueDate, submissionDueDate, targetLanguageInfos } = this.props;
      let existingDueDatesInfo = this.parseExistingDueDates(targetLanguageInfos);
      let { calculatedDueDates, targetLanguage } = this.state;
      // @ts-ignore
      let currentInfo = existingDueDatesInfo[targetLanguage];
      if (currentInfo) {
        calculatedDueDates = currentInfo;
      }
      this.setState({ existingDueDatesInfo, calculatedDueDates });
      // @ts-ignore
      let haveExistingDueDates = existingDueDatesInfo[targetLanguage]?.length;
      let batchDueDateChanged = prevProps.batchDueDate !== batchDueDate
      let submissionDueDateChanged = prevProps.submissionDueDate !== submissionDueDate;
      let phasedDataChanged = !_.isEqual(prevProps.phasesData, this.props.phasesData);
      if (((batchDueDateChanged && prevProps.batchDueDate > batchDueDate) || submissionDueDateChanged || phasedDataChanged || !calculatedDueDates.length) && !haveExistingDueDates) {
        if (this.dontResetPhases) {
          this.dontResetPhases = false;
        } else {
          let prevWorkflow = _.get(prevProps, 'workflowId.value');
          let workflow = _.get(this.props, 'workflowId.value');
          if (prevWorkflow !== workflow) {
            this.getCalculatedDueDates();
          }
        }
      }
    }

    if (propsChanged || !_.isEqual(prevState, this.state)) {
      if (this.props.showComponent && !prevProps.showComponent) {
        const el = this.domElement.current;
        if (el) {
          el.focus();
          el.addEventListener('keydown', this.onKeyDown);
        }
        this.props.focusControl.attachKeyDown(el);
      }
    }
  };

  /**
   * Apply due dates to all batches
   */
  applyToAllBatches = () => {
    const calculatedDueDates = _.cloneDeep(this.state.calculatedDueDates);
    const { workflowId } = this.props;
    let phaseDueDates = {
      workflowId: workflowId?.value,
      dueDates: calculatedDueDates
    }
    this.dispatch('applyPhaseDueDatesToAllBatches', '', { phaseDueDates })
  }

  /**
   * Apply due dates to all languages
   */
  applyToAllLanguagesHandler = () => {
    const { onChange, targetLanguages, moduleId } = this.props;
    const { calculatedDueDates } = this.state;

    let targetLanguagesLength = targetLanguages.length;
    let i = 0;
    let current;
    let e;
    let value = [];

    while (i < targetLanguagesLength) {
      current = targetLanguages[i];
      value.push({
        targetLanguage: current.targetLanguage,
        phasesDueDates: calculatedDueDates
      });
      i++;
    }

    e = {
      target: {
        props: {
          name: 'phaseDueDateMultiple'
        },
        value: value
      }
    };
    onChange(moduleId, undefined, e);
  }

  /**
   * Calculate new due dates
   */
  calculateDueDatesOnChange = (index: number, value: number) => {
    let calculatedDueDates = _.cloneDeep(this.state.calculatedDueDates);
    let dueDatesLength = calculatedDueDates.length;
    let current;
    while (index < dueDatesLength) {
      current = calculatedDueDates[index].dueDate;
      if (current < value) {
        calculatedDueDates[index].dueDate = value;
      }
      index++;
    }
    return calculatedDueDates;
  }

  /**
   * Clears all data regarding on blur
   */
  clearFocus = () => {
    this.setState({
      currentValue: null,
      index: null
    }, () => {
      let element = _.get(this.focusElement, 'target._dateInput.current._element');
      if (element) {
        element.focus();
      }
      this.focusElement = undefined;
    });
  };

  /**
   * Get calculated due dates from server side
   */
  getCalculatedDueDates = () => {
    const batchDueDate = getUnixDate(this.props.batchDueDate);
    const workflowId = this.props.workflowId?.value;
    const isDueDateValid = getUnixDate(new Date()) < getUnixDate(this.props.batchDueDate)
    if (batchDueDate && workflowId && isDueDateValid) {
      let params = { batchDueDate, workflowId };
      this.dispatch('getCalculatedDueDates', Position.end, { params, phasesModuleId: this.props.moduleId });
    } else {
      this.dispatch('unmaskModuleOnly', Position.end)
    }
  }

  /**
   * Checks if object contains some of given props if that's the case returns datePickerFormat else returns emptyPickerFormat as DatePickerFormat
   */
  getDateFormat = (value: Date | undefined): string => {
    const dateFormat = value ? this.translateByNamespace('date.datePickerFormat') : this.translate('emptyPickerFormat');
    return dateFormat
  };

  /**
   * Localized date picker on blur handler
   */
  localizedDatePickerOnBlur = () => {
    const {
      currentValue: value,
      index
    } = this.state;

    if (value && _.isNumber(index) && !this.calendarClick) {
      let { isValid, calculatedDueDates } = this.validateDateChange(index, value);

      if (isValid) {
        calculatedDueDates[index].dueDate = value;
        this.setCalculatedDueDateAfterChange(calculatedDueDates);
        this.focusElement = undefined;
      }
    }
    this.calendarClick = false;
  };

  /**
   * Date picker on change
   * validate and set calculatedDueDates
   */
  onChange = (index: number, event: DatePickerChangeEvent) => {
    const target = event.target;
    const value = getUnixDate(target.value);
    this.focusElement = event;
    this.calendarClick = event?.nativeEvent.type === "click";

    this.setState({
      currentValue: value,
      index
    });
  }

  /**
   * Handle leave focus out of popup
   *
   * @param e
   */
  private onKeyDown = (e: KeyboardEvent) => {
    const popupLeaveFocusEl = this.props.popupLeaveFocusEl;
    if (e.key.toLowerCase() === 'e' && e.ctrlKey && e.altKey && popupLeaveFocusEl) {
      popupLeaveFocusEl._element.focus();
    } else if (e.keyCode === Keys.esc) {
      e.preventDefault();
      const el = this.domElement.current;
      if (el) {
        el.removeEventListener('keydown', this.onKeyDown);
      }
      this.dispatch('closeExtra', '', {
        moduleId: this.props.moduleId
      });
      popupLeaveFocusEl._element && popupLeaveFocusEl._element.focus();
    }
  };

  /**
   * Adding class on focus
   */
  handleFocus = (event: ComboBoxFocusEvent | any) => {
    event.nativeEvent.target?.classList?.add('k-focused');
  }

  /**
   * Remove class on blur
   */
  handleBlur = (event: ComboBoxBlurEvent | any) => {
    event.nativeEvent.target?.classList?.remove('k-focused');
  }

  /**
   * Parse already existing due dates data
   */
  parseExistingDueDates = (dueDatesInfo: DUE_DATE_INFO[]) => {
    let parsedData = {};
    let dueDateInfosLength = dueDatesInfo.length;
    let i = 0;
    let current;
    while (i < dueDateInfosLength) {
      current = dueDatesInfo[i];
      // @ts-ignore
      parsedData[current.targetLanguage] = current.dueDateInfos;
      i++;
    }

    return parsedData;
  }

  /**
   * Set calculated due dates from server side
   * @param {DUE_DATE_INFO[]} calculatedDueDates
   */
  setCalculatedDueDates = (calculatedDueDates: DUE_DATE_INFO[] | []) => {
    this.setState({
      calculatedDueDates,
      currentValue: null,
      index: null
    });
    this.setCalculatedDueDateAfterChange(calculatedDueDates);
  }

  /**
   * Set calculated due dates after some change
   */
  setCalculatedDueDateAfterChange = (calculatedDueDates: DUE_DATE_INFO[]) => {
    const { moduleId, onChange } = this.props

    this.setState({
      calculatedDueDates,
      currentValue: null,
      index: null
    }, () => {
      let targetLanguage = this.state.targetLanguage;
      if (targetLanguage) {
        let e = {
          target: {
            props: {
              name: 'phaseDueDate'
            },
            value: {
              targetLanguage: targetLanguage,
              phasesDueDates: calculatedDueDates
            }
          }
        };
        onChange(moduleId, undefined, e);
      }
    });
  }

  /**
   * Target language change handler
   * @param event
   */
  targetLanguageChange = (event: ComboBoxChangeEvent) => {
    let value = event.target.value;
    let { calculatedDueDates, existingDueDatesInfo } = this.state;
    let currentInfo = existingDueDatesInfo[value];
    if (currentInfo?.length) {
      calculatedDueDates = currentInfo;
    } else {
      this.getCalculatedDueDates();
    }

    this.setState({
      targetLanguage: value,
      calculatedDueDates
    });
  }

  /**
   * Validate due date change and apply different rules
   */
  validateDateChange = (index: number, value: number) => {
    const { moduleId } = this.props;
    let isValid = true;
    const batchDueDate = getUnixDate(this.props.batchDueDate);
    const submissionDueDate = getUnixDate(this.props.submissionDueDate);
    let calculatedDueDates = [...this.state.calculatedDueDates];
    const previousPhaseDate = calculatedDueDates[index - 1]?.dueDate;
    const nextPhaseDate = calculatedDueDates[index + 1]?.dueDate;
    if (submissionDueDate < value) {
      //Phase due date exceeds the submission due date
      isValid = false;
      calculatedDueDates = this.calculateDueDatesOnChange(index, value);
      this.dontResetPhases = true;
      this.dispatch('changeSubmissionDueDateByPhaseDate', '', {
        moduleId,
        index,
        calculatedDueDates,
        newDateValue: value,
        fromDate: this.translateByNamespace('date.dateWithTime', {
          date: new Date(submissionDueDate)
        }),
        toDate: this.translateByNamespace('date.dateWithTime', {
          date: new Date(value)
        })
      });
    } else if (previousPhaseDate && previousPhaseDate > value) {
      //Phase due date is earlier than the phase due date of previous phase
      isValid = false;
      this.dispatch('warningPhaseDateWillReset', '', {
        moduleId
      });
    } else if (batchDueDate < value) {
      //Phase due date is after batch due date
      isValid = false;
      calculatedDueDates = this.calculateDueDatesOnChange(index + 1, value);
      this.dontResetPhases = true;
      this.dispatch('changeBatchDueDateByPhaseDate', '', {
        moduleId,
        index,
        calculatedDueDates,
        newDateValue: value,
        fromDate: this.translateByNamespace('date.dateWithTime', {
          date: new Date(submissionDueDate)
        }),
        toDate: this.translateByNamespace('date.dateWithTime', {
          date: new Date(value)
        })
      });
    } else if (nextPhaseDate && nextPhaseDate < value) {
      //Phase due date is after the phase due date of the following phase
      isValid = true;
      calculatedDueDates = this.calculateDueDatesOnChange(index + 1, value);
    }

    return { isValid, calculatedDueDates };
  }

  render() {
    const {
      calculatedDueDates,
      index: dueDateElementIndex,
      currentValue,
      targetLanguage
    } = this.state;
    const { targetLanguages, multipleBatches, allowEdit, showComponent } = this.props;
    return (
      showComponent ? (
        <div
          ref={this.domElement}
          className={'tp-custom-popup-wrap'} tabIndex={0}
          onKeyDown={(e) => this.props.focusControl.preserveFocusOnPopup(this.domElement.current, e)}
        >
          <div className={'tp-custom-popup-abstract tp-batch-phase-due-dates-popup k-hbox k-vbox'}>
            <div className={'tp-custom-popup-header k-hbox k-align-items-center k-justify-content-between'}>
              <Container
                htmlTag={'h3'}
                className='tp-text-semibold tp-custom-popup-header-title tp-font-size'
                displayInlineFlex
              >
                {this.translate('title')}
              </Container>
              <FilterCombo
                label={this.translate('title')}
                labelClassName={'sr-only'}
                size={'small'}
                data={targetLanguages}
                name={'language'}
                onBlur={this.handleBlur}
                onFocus={this.handleFocus}
                textField='targetLanguageDisplayName'
                valueField='targetLanguage'
                value={targetLanguage}
                translateMethod={this.translate.bind(this)}
                onChange={this.targetLanguageChange}
                valueAsSimpleType={true}
              />
            </div>
            <div className={`tp-custom-popup-body ${calculatedDueDates.length > 5 ? 'tp-has-scroll' : ''}`}>
              {targetLanguage && calculatedDueDates.map((item: DUE_DATE_INFO, index: number) => {
                let phaseName = item.phaseName;
                let value = new Date(item.dueDate);
                if (index === dueDateElementIndex && currentValue) {
                  value = new Date(currentValue);
                }
                return (
                  <div key={index} className={'tp-batch-phase-item k-hbox k-align-items-center'}>
                    <span
                      className={'tp-batch-phase-name tp-font-size-sm tp-text-medium k-hbox k-justify-content-end'}>
                      {item.phaseName}
                    </span>
                    <span className={'tp-batch-phase-date k-small'}>
                      <LocalizedDatePicker
                        className={'tp-without-error-text'}
                        disabled={item.completed || !allowEdit}
                        format={this.getDateFormat(value)}
                        label={this.translate('SRL-dueDate', { phase: item.phaseName })}
                        min={new Date()}
                        size={'small'}
                        name={phaseName}
                        onChange={this.onChange.bind(this, index)}
                        onBlur={this.localizedDatePickerOnBlur}
                        value={value}
                      />
                    </span>
                  </div>
                )
              })}
            </div>
            <Container gap={2} displayFlex className={'tp-custom-popup-footer tp-transparent'}>
              {targetLanguage && targetLanguages.length > 1 && (
                <PuzzleButton
                  themeColor={'primary'}
                  onClick={this.applyToAllLanguagesHandler}
                >
                  {this.translate('applyToAllLanguages')}
                </PuzzleButton>
              )}
              {multipleBatches && (
                <PuzzleButton
                  themeColor={'primary'}
                  onClick={this.applyToAllBatches}
                >
                  {this.translate('applyToAllBatches')}
                </PuzzleButton>
              )}
            </Container>
          </div>
        </div>
      ) : null
    );
  }
}

export default React.memo(FocusControl(PhaseDueDates)); //PDII-36870
